let MaterialHandler = function (___settings, ___handlers) {
  const THREE = require('../../../../../externals/three'),
        GLOBAL_UTILS = require('../../../../../shared/util/GlobalUtils'),
        ExpoTHREE = ___settings.externalLibraries.ExpoTHREE,
        EXPOASSETUTILS = require('../../../../../externals/expo-asset-utils'),
        MaterialHandlerInterface = require('../../../../interfaces/handlers/MaterialHandlerInterface'),
        envMapUrlPrefix = 'https://viewer.shapediver.com/v2/envmaps/',
        envMapNamesJPG = [
          'default',
          'default_bw',
          'blurred_lights',
          'georgentor',
          'georgentor_blur',
          'georgentor_blue_blur',
          'georgentor_bw_blur',
          'levelsets',
          'lythwood_field',
          'mountains',
          'ocean',
          'piazza_san_marco',
          'residential_garden',
          'room_abstract_1',
          'sky',
          'storage_room',
          'storm',
          'subway_entrance',
          'subway_entrance_bw_blur',
          'white',
          'yokohama',
        ],
        envMapNamesPNG = [],
        envMapFilenames = ['px', 'nx', 'pz', 'nz', 'py', 'ny'],
        _settings = ___settings.settings,
        _handlers = ___handlers;

  let that,
      _helpers,
      _sceneBackground,
      _environmentMap = null,
      _usedEnvironmentMaps = [],
      _testMaterial = new THREE.MeshStandardMaterial(),
      _active = true;


  /**
   * Changes the environment by calling the appropriate function in ShapeDiverMaterialLoader.
   * The viewer will be set to busy while it is loading.
   *
   * @param  {String} name  Can be the name of a predefined environment map like
   *                        'default','default_bw','blurred_lights','georgentor','georgentor_blur','georgentor_blue_blur',
   *                        'georgentor_bw_blur','mountains','ocean','piazza_san_marco','room_abstract_1','sky',
   *                        'storm','subway_entrance','subway_entrance_bw_blur','white','yokohama'
   *                        or an array of urls ('px','nx','pz','nz','py','ny').
   * @returns {Promise<Boolean>} Returns a promise, which will resolve to true or false depending on success
   */
  let _environmentMapHook = function (name) {
    let scope = 'MaterialHandler.setEnvironmentMap';
    if (name === undefined) {
      _handlers.threeDManager.warn(scope, 'No input was provided, the environment map was not set.');
      return Promise.resolve(false);
    }
    let name_internal, url;

    // check if name is a JSON.stringified version of an array of urls
    if ( GLOBAL_UTILS.typeCheck(name, 'string') && name.startsWith('["https') && name.endsWith('"]') ) {
      let name_orig = name;
      try {
        name = JSON.parse(name_orig);
      } catch (e) {
        name = name_orig;
      }
    }

    // deal with string or array
    if (GLOBAL_UTILS.typeCheck(name, 'string')) {
      name_internal = name.toLowerCase().replace(/ /g, '_');
    } else if (Array.isArray(name)) {
      url = name;
      if (url.length !== 6) {
        _handlers.threeDManager.warn(scope, 'The number of URLs was not 6, the environment map was not set.');
        return Promise.resolve(false);
      }

      name_internal = JSON.stringify(url, null, 0);
    }

    for (let envMap of _usedEnvironmentMaps) {
      if (envMap.name === name_internal) {
        that._assignEnvironmentMap(envMap);
        return Promise.resolve(true);
      }
    }

    if (url === undefined) {
      url = [];
      let i;
      if (envMapNamesJPG.indexOf(name_internal) >= 0) {
        // found in list of available environment maps with file type jpg
        for (i = 0; i < envMapFilenames.length; i++)
          url.push(envMapUrlPrefix + name_internal + '/' + envMapFilenames[i] + '.jpg');
      }
      else if (envMapNamesPNG.indexOf(name_internal) >= 0) {
        // found in list of available environment maps with file type png
        for (i = 0; i < envMapFilenames.length; i++)
          url.push(envMapUrlPrefix + name_internal + '/' + envMapFilenames[i] + '.png');
      }
      else if (name.startsWith('https://') || name.startsWith('http://')) {
        if (!name.endsWith('/')) {
          name += '/';
        }
        for (i = 0; i < envMapFilenames.length; i++)
          url.push(name + envMapFilenames[i] + '.jpg');
      }
      else {
        _handlers.threeDManager.warn(scope, 'Environment map name not found, and not a base URL');
        return Promise.resolve(false);
      }
    }

    return that._loadEnvironmentMap(name_internal, url).then(function (response) {
      _usedEnvironmentMaps.push(response);
      that._assignEnvironmentMap(response);
      return true;
    }, function () {
      _handlers.threeDManager.warn(scope, 'Was not able to load the environment map, the environment map was not set.');
      return false;
    });
  };


  /**
   * Toggles the environment map as background.
   *
   * @param {Boolean} toggle To what to set the mode
   * @returns {Boolean} If the mode was successfully set
   */
  let _environmentMapAsBackgroundHook = function (toggle) {
    let scope = 'MaterialHandler.Hook->environmentMapAsBackground';
    if (!GLOBAL_UTILS.typeCheck(toggle, 'boolean', _handlers.threeDManager.warn, scope)) return false;

    if (_handlers.threeDManager.getSetting('groundPlaneReflectionVisibility') && toggle) {
      _handlers.threeDManager.warn(scope, 'The ground plane reflection and the environment as background do not work together right now.');
      return false;
    }

    if (toggle) {
      _sceneBackground = _environmentMap;
    } else {
      _sceneBackground = null;
    }
    return true;
  };

  class MaterialHandler extends MaterialHandlerInterface {

    constructor() {
      super();
      that = this;

      _helpers = new (require('../../../../helpers/handlers/MaterialHandlerHelpers'))(_handlers, {
        nameStandard: 'MeshStandardMaterial',
        nameSimple: 'MeshPhongMaterial'
      });

      _settings.registerHook('environmentMap', _environmentMapHook);
      _settings.updateSettingAsync('environmentMap', _settings.getSetting('environmentMap'));
      _settings.registerHook('environmentMapAsBackground', _environmentMapAsBackgroundHook);
    }

    /**
     * Tries to load an environment map with the given urls and returns a
     * promise with the environment map if it succeded.
     *
     * @param {String} name The name of the environment map
     * @param {String[]} url The urls for the environment map
     * @returns {Promise} Promise with the environment map and name if succeded
     */
    _loadEnvironmentMap(name, url) {

      let envMapUrls = {
        'px': url[0],
        'nx': url[1],
        'pz': url[2],
        'nz': url[3],
        'py': url[4],
        'ny': url[5]
      };

      let promises = [];
      for(let x in envMapUrls) {
      // get file extension from content type
        let extension = 'jpg';
        let opt = { fileName: 'envMap' + x + '.' + extension };
        promises.push(new Promise(function (resolve, reject) {
          EXPOASSETUTILS.resolveAsync(envMapUrls[x], opt)
            .then(
              function (texture) {
                envMapUrls[x] = texture.localUri;
                resolve(texture);
              },
              function (error) {
                reject(error);
              }
            );
        }
        ));
      }

      return Promise.all(promises).then(function() {
        return ExpoTHREE.loadCubeTextureAsync({
          assetForDirection: envMapUrls,
          directions: ['px', 'nx', 'pz', 'nz', 'py', 'ny']
        }).then(function (environmentMap) {
          return {
            name: name,
            map: environmentMap
          };
        });
      });
    }

    /**
     * Assigns the enviornment map to the current selection.
     * Adds the environment map to the _scene.background, if activated.
     * Updates all materials.
     *
     * @param {Object} mapObject The environment map object with the name and the map
     */
    _assignEnvironmentMap(mapObject) {
      _environmentMap = mapObject.map;
      if (_settings.getSetting('environmentMapAsBackground')) _sceneBackground = _environmentMap;

      let materials = _handlers.threeDManager.helpers.getMaterials();
      for (let i = 0, len = materials.length; i < len; i++) {
        let mat = materials[i];
        if ((mat.type == 'MeshStandardMaterial') && mat.envMap) {
          mat.envMap = _environmentMap;
          //mat.needsUpdate = true;
        }
      }
    }

    ////////////
    ////////////
    //
    // MaterialHandler API
    //
    ////////////
    ////////////
    
    /** @inheritdoc */
    pause() {
      _active = false;
    }

    /** @inheritdoc */
    resume() {
      _active = true;
    }

    /** @inheritdoc */
    destroy() {
      // destroy the material handler
    }




    /** @inheritdoc */
    getMaterial(properties) {
      let temp = {};
      let prop = Object.assign({envMap: _environmentMap}, properties);
      for(let p in prop)
        if(_testMaterial.hasOwnProperty(p))
          temp[p] = prop[p];

      return new THREE.MeshStandardMaterial(temp);
    }

    /** @inheritdoc */
    getSimpleMaterial(properties) {
      return new THREE.MeshPhongMaterial(properties);
    }

    /** @inheritdoc */
    compile() {
      _handlers.renderingHandler.renderHidden();
      _helpers.checkMaterials();
    }
    



    /** @inheritdoc */
    adjustToBoundingSphere(bs) {
      _handlers.threeDManager.debug('This method is not implemented in this viewport.');
    }




    /** @inheritdoc */
    getEnvironmentMap() {
      _handlers.threeDManager.debug('This method is not implemented in this viewport.');
    }

    /** @inheritdoc */
    setSceneBackground(b) {
      _sceneBackground = b;
    }

    /** @inheritdoc */
    getSceneBackground() {
      return _sceneBackground;
    }
  }

  return new MaterialHandler();
};

module.exports = MaterialHandler;
